if (!customElements.get('click-collect')) {
  customElements.define(
    'click-collect',
    class ClickCollect extends HTMLElement {
      constructor() {
        super();

        this.productId = this.dataset.productId;
        this.storageKey = `click-collect:${this.productId}`;
        this.readyLabel = this.dataset.tabReadyLabel || 'Pick up ready';
        this.workshopLabel = this.dataset.tabWorkshopLabel || 'Build in store';
        this.soldOutMessage = this.dataset.soldOutMessage || 'This store is sold out – choose another';
        this.infoLabel = this.dataset.infoLabel || 'Info';
        this.hoursLabel = this.dataset.hoursLabel || 'Opening hours';
        this.addressLabel = this.dataset.addressLabel || 'Address';
        this.statusInStockLabel = this.dataset.statusInStockLabel || 'In stock';
        this.statusSoldOutLabel = this.dataset.statusSoldOutLabel || 'Sold out';
        this.statusPreorderLabel = this.dataset.statusPreorderLabel || 'Pre-order';
        this.isPreorder = this.dataset.isPreorder === 'true';

        this.activeTab = 'ready';
        this.selected = null;
        this.userCoords = null;

        this.modal = this.querySelector('basic-modal');
        this.list = this.querySelector('.click-collect__list');
        this.confirmBtn = this.querySelector('.click-collect__confirm-btn');
        this.summary = this.querySelector('.click-collect__summary');
        this.confirmDefaultLabel = this.confirmBtn ? this.confirmBtn.textContent.trim() : 'Confirm store';

        // Cached up front because <basic-modal> detaches from <click-collect> and moves to
        // <body> when shown (see BasicModal.requiresBodyAppended in theme.js). Re-querying
        // `this.querySelectorAll(...)` after that point would always return an empty list,
        // since the tabs/info text no longer live inside <click-collect>'s subtree.
        this.tabButtons = Array.from(this.querySelectorAll('[data-cc-tab]'));
        this.infoParagraphs = Array.from(this.querySelectorAll('[data-cc-info]'));

        this.stores = this.readStoreData();

        this.bindTabs();
        this.bindConfirm();
        this.renderList();
        this.restoreSelection();

        if (navigator.geolocation) {
          navigator.geolocation.getCurrentPosition((pos) => {
            this.userCoords = { lat: pos.coords.latitude, lng: pos.coords.longitude };
            this.renderList();
          });
        }
      }

      readStoreData() {
        const script = document.querySelector(`script[data-click-collect-stores="${this.productId}"]`);
        if (!script) return [];
        try {
          return JSON.parse(script.textContent);
        } catch (e) {
          return [];
        }
      }

      distanceKm(store) {
        if (!this.userCoords || store.lat == null || store.lng == null) return null;
        const R = 6371;
        const dLat = ((store.lat - this.userCoords.lat) * Math.PI) / 180;
        const dLng = ((store.lng - this.userCoords.lng) * Math.PI) / 180;
        const a =
          Math.sin(dLat / 2) ** 2 +
          Math.cos((this.userCoords.lat * Math.PI) / 180) *
            Math.cos((store.lat * Math.PI) / 180) *
            Math.sin(dLng / 2) ** 2;
        return R * 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
      }

      sortedStores() {
        const withDistance = this.stores.map((s) => ({ ...s, distance: this.distanceKm(s) }));
        if (this.userCoords) {
          withDistance.sort((a, b) => (a.distance ?? Infinity) - (b.distance ?? Infinity));
        }
        return withDistance;
      }

      bindTabs() {
        this.tabButtons.forEach((tab) => {
          tab.addEventListener('click', () => {
            this.activeTab = tab.dataset.ccTab;
            this.tabButtons.forEach((t) => {
              const isActive = t === tab;
              t.classList.toggle('is-active', isActive);
              t.setAttribute('aria-selected', String(isActive));
            });
            this.infoParagraphs.forEach((info) => {
              info.hidden = info.dataset.ccInfo !== this.activeTab;
            });
            this.updateConfirmState();
          });
        });
      }

      renderList() {
        if (!this.list) return;
        const stores = this.sortedStores();
        this.list.innerHTML = stores.map((store, index) => this.storeRowHtml(store, index)).join('');

        this.list.querySelectorAll('[data-cc-select]').forEach((card) => {
          const select = () => {
            const idx = Number(card.dataset.ccSelect);
            this.selected = stores[idx];
            this.list.querySelectorAll('.click-collect__store').forEach((el) => {
              const isThis = el === card;
              el.classList.toggle('is-selected', isThis);
              el.setAttribute('aria-pressed', String(isThis));
            });
            this.updateConfirmState();
          };
          card.addEventListener('click', (e) => {
            if (e.target.closest('[data-cc-toggle]')) return;
            select();
          });
          card.addEventListener('keydown', (e) => {
            if ((e.key === 'Enter' || e.key === ' ') && !e.target.closest('[data-cc-toggle]')) {
              e.preventDefault();
              select();
            }
          });
        });

        this.list.querySelectorAll('[data-cc-toggle]').forEach((btn) => {
          btn.addEventListener('click', (e) => {
            e.stopPropagation();
            const item = btn.closest('.click-collect__store-item');
            const detail = item ? item.querySelector('.click-collect__detail') : null;
            const icon = btn.querySelector('.click-collect__toggle-icon');
            const expanded = btn.getAttribute('aria-expanded') === 'true';
            btn.setAttribute('aria-expanded', String(!expanded));
            if (icon) icon.textContent = expanded ? '+' : '−';
            if (detail) detail.hidden = expanded;
          });
        });

        if (this.selected) {
          this.highlightSelected(stores);
        }
      }

      highlightSelected(stores) {
        if (!this.selected) return;
        const idx = stores.findIndex((s) => s.id === this.selected.id);
        if (idx === -1) return;
        const card = this.list.querySelector(`[data-cc-select="${idx}"]`);
        if (card) {
          card.classList.add('is-selected');
          card.setAttribute('aria-pressed', 'true');
        }
      }

      storeRowHtml(store, index) {
        const distance =
          store.distance != null
            ? (store.distance < 10 ? store.distance.toFixed(1) : Math.round(store.distance)) + ' km'
            : '';
        // "Sell when out of stock" products always report available: true for pickup,
        // even with zero physical inventory — show "Pre-order" instead of "In stock"
        // for those, without changing selection behavior (still not sold-out/blocked).
        const statusClass = this.isPreorder ? 'is-preorder' : store.available ? 'is-available' : 'is-soldout';
        const statusLabel = this.isPreorder
          ? this.statusPreorderLabel
          : store.available
            ? this.statusInStockLabel
            : this.statusSoldOutLabel;
        const address = [store.street, [store.zip, store.city].filter(Boolean).join(' ')]
          .filter((line) => line && line.trim())
          .join('\n');
        const hasDetail = Boolean(store.hours || address);
        const img = store.image ? `<img src="${this.escape(store.image)}" alt="" loading="lazy">` : '';

        return `
          <div class="click-collect__store-item" role="listitem">
            <div class="click-collect__store" data-cc-select="${index}" tabindex="0" role="button" aria-pressed="false">
              <div class="click-collect__store-thumb">${img}</div>
              <div class="click-collect__store-body">
                <div class="click-collect__store-head">
                  <span class="click-collect__store-name">${this.escape(store.name)}</span>
                  ${distance ? `<span class="click-collect__store-distance">${distance}</span>` : ''}
                </div>
                ${store.pickupTime ? `<div class="click-collect__store-time">${this.escape(store.pickupTime)}</div>` : ''}
                ${store.deliveryInfo ? `<div class="click-collect__store-note">${this.escape(store.deliveryInfo)}</div>` : ''}
                <div class="click-collect__store-status">
                  <span class="click-collect__dot ${statusClass}" aria-hidden="true"></span>
                  <span class="click-collect__status-label ${statusClass}">${statusLabel}</span>
                  ${
                    hasDetail
                      ? `<button type="button" class="click-collect__toggle" data-cc-toggle aria-expanded="false" aria-label="Show opening hours and address">
                          <span class="click-collect__toggle-label">${this.escape(this.infoLabel)}</span>
                          <span class="click-collect__toggle-icon" aria-hidden="true">+</span>
                        </button>`
                      : ''
                  }
                </div>
              </div>
            </div>
            ${
              hasDetail
                ? `<div class="click-collect__detail" hidden>
                    ${store.hours ? `<div><dt>${this.escape(this.hoursLabel)}</dt><dd>${this.escape(store.hours)}</dd></div>` : ''}
                    ${address ? `<div><dt>${this.escape(this.addressLabel)}</dt><dd>${this.escape(address)}</dd></div>` : ''}
                  </div>`
                : ''
            }
          </div>
        `;
      }

      decodeEntities(value) {
        if (value == null) return '';
        // Some location names/addresses come from Shopify already containing literal HTML
        // entities (e.g. "&amp;" typed or imported as text rather than a real "&").
        const decodeArea = document.createElement('textarea');
        decodeArea.innerHTML = String(value);
        return decodeArea.value;
      }

      escape(value) {
        if (value == null) return '';
        const div = document.createElement('div');
        div.textContent = this.decodeEntities(value);
        return div.innerHTML;
      }

      updateConfirmState() {
        if (!this.confirmBtn) return;
        const blockedByStock = this.activeTab === 'ready' && this.selected && !this.selected.available;
        this.confirmBtn.disabled = !this.selected || blockedByStock;
        this.confirmBtn.textContent = blockedByStock ? this.soldOutMessage : this.confirmDefaultLabel;
      }

      bindConfirm() {
        if (!this.confirmBtn) return;
        this.confirmBtn.addEventListener('click', () => {
          if (!this.selected || this.confirmBtn.disabled) return;
          this.persistSelection();
          this.renderSummary();
          if (this.modal && typeof this.modal.hide === 'function') this.modal.hide();

          const form = document.querySelector(`.main-product-form[data-product-id="${this.productId}"]`);
          // On bear products, an invisible full-cover layer (.trigger-certificate-modal-button)
          // sits on top of the real "Add to cart" button and is what an actual mouse click
          // lands on first — clicking the button underneath directly would skip the
          // certificate step entirely. Click whichever one a real user click would actually hit.
          const certificateTrigger = form ? form.querySelector('.trigger-certificate-modal-button') : null;
          const submitBtn = form ? form.querySelector('[type="submit"]') : null;
          const clickTarget = certificateTrigger || submitBtn;
          if (clickTarget) {
            // Small delay so the modal's own close animation isn't fighting whatever opens
            // next (cart drawer or the certificate popup).
            setTimeout(() => clickTarget.click(), 300);
          }
        });
      }

      persistSelection() {
        try {
          window.localStorage.setItem(this.storageKey, JSON.stringify({ tab: this.activeTab, store: this.selected }));
        } catch (e) {
          /* localStorage unavailable — selection just won't persist across reloads */
        }
        this.updateLineItemProperties();
      }

      updateLineItemProperties() {
        const methodInput = document.getElementById(`ClickCollectPropertyMethod-${this.productId}`);
        const storeInput = document.getElementById(`ClickCollectPropertyStore-${this.productId}`);
        if (!methodInput || !storeInput) return;
        if (!this.selected) {
          methodInput.value = '';
          storeInput.value = '';
          return;
        }
        methodInput.value = this.activeTab === 'workshop' ? this.workshopLabel : this.readyLabel;
        storeInput.value = this.decodeEntities(this.selected.name);
      }

      restoreSelection() {
        let saved = null;
        try {
          const raw = window.localStorage.getItem(this.storageKey);
          if (raw) saved = JSON.parse(raw);
        } catch (e) {
          saved = null;
        }
        if (!saved || !saved.store) return;

        const stillExists = this.stores.some((s) => s.id === saved.store.id);
        if (!stillExists) {
          this.clearPersistedSelection();
          return;
        }

        // A saved choice only means the customer picked a store at some point — it doesn't
        // mean the product is still in the cart (it may have been removed, or this could be a
        // later visit entirely). Only show the "confirmed" summary if the product is actually
        // in the cart right now with this pickup store attached.
        fetch(`${window.Shopify?.routes?.root || '/'}cart.js`)
          .then((response) => response.json())
          .then((cart) => {
            const inCart = (cart.items || []).some(
              (item) =>
                String(item.product_id) === String(this.productId) &&
                item.properties &&
                item.properties['Pickup store'] === this.decodeEntities(saved.store.name)
            );
            if (!inCart) {
              this.clearPersistedSelection();
              return;
            }

            this.activeTab = saved.tab === 'workshop' ? 'workshop' : 'ready';
            this.selected = this.stores.find((s) => s.id === saved.store.id) || saved.store;

            this.tabButtons.forEach((t) => {
              const isActive = t.dataset.ccTab === this.activeTab;
              t.classList.toggle('is-active', isActive);
              t.setAttribute('aria-selected', String(isActive));
            });
            this.infoParagraphs.forEach((info) => {
              info.hidden = info.dataset.ccInfo !== this.activeTab;
            });

            this.renderList();
            this.updateConfirmState();
            this.renderSummary();
            this.updateLineItemProperties();
          })
          .catch(() => {
            /* If we can't verify the cart, don't show a possibly-stale summary. */
          });
      }

      clearPersistedSelection() {
        try {
          window.localStorage.removeItem(this.storageKey);
        } catch (e) {
          /* ignore */
        }
      }

      renderSummary() {
        if (!this.summary || !this.selected) return;
        const typeLabel = this.activeTab === 'workshop' ? this.workshopLabel : this.readyLabel;
        const storeEl = this.summary.querySelector('.click-collect__summary-store');
        const metaEl = this.summary.querySelector('.click-collect__summary-meta');
        if (storeEl) storeEl.textContent = this.decodeEntities(this.selected.name);
        if (metaEl) metaEl.textContent = [typeLabel, this.selected.pickupTime].filter(Boolean).join(' · ');
        this.summary.hidden = false;
      }
    }
  );
}
